import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation } from 'swiper/modules';
import { getActivePartners } from '../lib/supabase';
import 'swiper/css';
import 'swiper/css/navigation';

interface Partner {
  id?: string;
  name: string;
  logo: string;
  website?: string;
  tier?: string;
}

function SponsorsSection() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await getActivePartners();
      if (error) throw error;

      if (data && data.length > 0) {
        const allPartners = data.map(p => ({
          id: p.id,
          name: p.company_name,
          logo: p.logo_url || `https://placehold.co/200x100/png?text=${encodeURIComponent(p.company_name)}`,
          website: p.website || '#',
          tier: p.tier
        }));

        const tierOrder = {
          'Tournament Host': 1,
          'Diamond': 2,
          'Title Sponsor': 2,
          'Platinum': 3,
          'Gold': 4,
          'Silver': 5,
          'Bronze': 6
        };

        allPartners.sort((a, b) => {
          const tierA = a.tier ? tierOrder[a.tier as keyof typeof tierOrder] || 99 : 99;
          const tierB = b.tier ? tierOrder[b.tier as keyof typeof tierOrder] || 99 : 99;
          return tierA - tierB;
        });

        setPartners(allPartners);
      } else {
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-golf-sand/30 to-transparent" />
      
      <div className="container mx-auto px-4 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          <div className="text-center mb-16">
            <h2 className="section-heading text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-display font-bold text-golf-gray inline-block">
              Our Partners
            </h2>
          </div>
          
          {/* All Partners in a Single Row */}
          <div className="mb-20">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500">Error loading partners: {error}</p>
                <button 
                  onClick={fetchPartners}
                  className="mt-4 text-golf-orange hover:text-golf-green"
                >
                  Try Again
                </button>
              </div>
            ) : partners.length > 0 ? (
              <SwiperComponent
                modules={[Autoplay, Navigation]}
                spaceBetween={30}
                slidesPerView={1}
                breakpoints={{
                  640: { slidesPerView: 2 },
                  768: { slidesPerView: 3 },
                  1024: { slidesPerView: 5 }
                }}
                autoplay={{
                  delay: 2000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true
                }}
                loop={partners.length > 5}
                navigation={true}
                className="sponsors-swiper"
              >
                {partners.map((partner, index) => (
                  <SwiperSlide key={partner.id || index}>
                    <div
                      whileHover={{ y: -5, scale: 1.02 }}
                      className="sponsor-card p-6 h-full flex flex-col items-center justify-center"
                    >
                      <div className="h-20 flex items-center justify-center mb-3">
                        {partner.website && partner.website !== '#' ? (
                          <a 
                            href={partner.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <img
                              src={partner.logo}
                              alt={partner.name}
                              className="max-h-20 max-w-full object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = `https://placehold.co/200x100/png?text=${encodeURIComponent(partner.name)}`;
                              }}
                            />
                          </a>
                        ) : (
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-h-20 max-w-full object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = `https://placehold.co/200x100/png?text=${encodeURIComponent(partner.name)}`;
                            }}
                          />
                        )}
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-golf-gray text-center line-clamp-1">{partner.name}</h3>
                      <p className="text-sm text-golf-orange font-medium text-center mt-1">{partner.tier}</p>
                    </div>
                  </SwiperSlide>
                ))}
              </SwiperComponent>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No partners available yet.</p>
              </div>
            )}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="animated-border inline-block p-[2px]">
              <div className="bg-white px-6 sm:px-8 md:px-12 py-6 sm:py-8 rounded-2xl">
                <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold mb-4 sm:mb-6 text-golf-gray">
                  Become a Partner
                </h3>
                <p className="text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto text-sm sm:text-base">
                  Join us in shaping the future of golf in Africa. Partner with the Ibom Open 2025
                  and be part of this prestigious tournament.
                </p>
                <Link
                  to="/sponsors"
                  className="modern-button inline-block px-8 sm:px-12 py-3 sm:py-4 text-lg sm:text-xl text-white font-medium"
                >
                  Partnership Opportunities
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

export default SponsorsSection;